/**
 * BMR Details (/batches) — the list reads as promises (EXV-008).
 *
 * The api helpers are callback-based and used to swallow failures; each read here resolves with one page
 * ({ rows, total, pageSize }) or rejects with the failure, so a list can show "couldn't load" with Retry instead of
 * reading the failure as "no data". The page size is the server's (`page_size` on every page) — the client never
 * assumes one.
 */
import {
  get_api_function,
  get_completed_batch_details,
  get_execution_mbmr_options,
  get_ongoing_batch_list,
  get_release_approval_details,
  get_summary_dashboard,
} from '@/utils/api';

/** A callback helper `(params, success, onError)` as a promise of the response body; a "failed" body rejects. */
const asPromise = (helper, params) =>
  new Promise((resolve, reject) => {
    helper(
      params,
      (res) => {
        const body = res?.data;
        if (body?.status === 'success') resolve(body);
        else reject({ response: { status: res?.status, data: body } });
      },
      reject
    );
  });

const page = (body, rows) => ({
  rows: Array.isArray(rows) ? rows : [],
  total: Number(body?.obj_count) || 0,
  pageSize: Number(body?.page_size) || undefined,
});

/** The list parameters every Execution list takes: page, module, recipe scope, MBMR, schedule-date range, search. */
export const listParams = ({ page: pageNumber, moduleType, recipeType, filters = {} }) => ({
  page_number: pageNumber || 1,
  module_type: moduleType,
  ...(recipeType !== undefined && recipeType !== null ? { recipe_type: recipeType } : {}),
  ...(filters.recipeId ? { recipe_id: filters.recipeId } : {}),
  ...(filters.from && filters.to ? { start_time: filters.from, end_time: filters.to } : {}),
  ...(filters.search ? { search: filters.search } : {}),
});

export const fetchOngoing = (params) =>
  asPromise(get_ongoing_batch_list, params).then((b) => page(b, b.data));

/**
 * The Completed list's own filters (owner, 8 Oct 2026) as server parameters — the server filters the whole set, so
 * paging stays right. `value` = { status, releasedBy, releasedFrom, releasedTo }.
 */
export const completedParams = (value = {}) => ({
  ...(value.status ? { status: value.status } : {}),
  ...(value.releasedBy ? { released_by: value.releasedBy } : {}),
  ...(value.releasedFrom && value.releasedTo ? { released_from: value.releasedFrom, released_to: value.releasedTo } : {}),
});

/** One Completed page; with `filter_options` the server also names the status words and people present. */
export const fetchCompleted = (params) =>
  asPromise(get_completed_batch_details, params).then((b) => ({
    ...page(b, b.data?.[0]?.completed_list),
    filterOptions: b.filter_options || null,
  }));

export const fetchRejected = (params) =>
  asPromise((p, ok, ko) => get_api_function('/get_rejected_batche', ok, p, undefined, ko), params).then((b) =>
    page(b, b.data?.[0]?.completed_list)
  );

/**
 * EXV-ISSUE-2: `scope` 'mine' (default, the caller's roles) or 'all' (every pending row of the plant, read only — the
 * server answers 403 without read on the module). Each row carries `waiting_on` and `can_act`; the page also says
 * whether the caller may open 'all' and, if so, whom the plant's queue waits on.
 */
export const fetchIssueApproval = (params) =>
  asPromise(get_release_approval_details, params).then((b) => ({
    ...page(b, b.data),
    canSeeAll: !!b.can_see_all,
    waitingOnRoles: Array.isArray(b.waiting_on_roles) ? b.waiting_on_roles : [],
  }));

export const fetchSummary = (params) => asPromise(get_summary_dashboard, params).then((b) => b.data || {});

export const fetchMbmrOptions = (params) => asPromise(get_execution_mbmr_options, params).then((b) => b.data || []);

/** The words a failed read shows — by status, never the raw error text. */
export const failureMessage = (err, what) => {
  const status = err?.response?.status;
  if (status === 401) return 'Your session has ended. Sign in again to see this list.';
  if (status === 403) {
    // W1-9: a refusal is named — the module and flag the server's `module_access_denied` body states.
    const body = err?.response?.data || {};
    if (body.module && body.required) return `You do not hold ${body.required} access on “${body.module}”, so you cannot see ${what}.`;
    return `You do not have access to ${what}.`;
  }
  if (status === 404) return `${what[0].toUpperCase()}${what.slice(1)} could not be found.`;
  if (status >= 500) return `The server could not load ${what}. Please try again.`;
  return `We couldn’t load ${what}. Check the connection and try again.`;
};
